// =============================================================================
// errorMessage — normalize any thrown value into a human-readable string.
//
// Background: catch handlers around the codebase do
//   `e instanceof Error ? e.message : String(e)`
// which produces "[object Object]" when the thrown value is a
// PostgrestError, a plain object with a .message field, or any non-Error
// object — which Supabase clients throw all the time.
//
// This helper handles the common shapes (Error, Supabase error object,
// plain string, anything else) and falls back to JSON so the value at
// least carries information instead of being literally "[object Object]".
// =============================================================================

export function errorMessage(e: unknown): string {
  if (e == null) return 'Unknown error';
  if (typeof e === 'string') return e;
  if (e instanceof Error && e.message) return e.message;

  // Supabase PostgrestError, FunctionsHttpError, custom { message: ... }
  // objects, fetch Response-style errors, etc.
  if (typeof e === 'object') {
    const obj = e as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message)  return obj.message;
    if (typeof obj.error   === 'string' && obj.error)    return obj.error;
    if (typeof obj.details === 'string' && obj.details)  return obj.details;
    if (typeof obj.hint    === 'string' && obj.hint)     return obj.hint;
    try {
      const json = JSON.stringify(obj);
      // JSON.stringify on an object with no enumerable own properties
      // returns "{}" — fall back to String() so the consumer at least
      // sees the constructor name (e.g. "FunctionsHttpError").
      return json === '{}' ? String(obj) : json;
    } catch {
      return String(obj);
    }
  }

  return String(e);
}
